import React, { useState } from 'react';
import { Button, Upload, Modal, message, Space, Typography, Alert } from 'antd';
import {
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { exportAPI, importAPI } from '../services/api';

const { Dragger } = Upload;
const { Text } = Typography;

/**
 * 导入导出按钮组件
 * @param {string} type - 数据类型 (products/customers/suppliers)
 * @param {object} exportParams - 导出时的筛选参数
 * @param {function} onImportSuccess - 导入成功后的回调
 */
const ImportExportButtons = ({ type, exportParams = {}, onImportSuccess }) => {
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // 类型配置
  const typeConfig = {
    products: { name: '商品', importFn: importAPI.products },
    customers: { name: '客户', importFn: importAPI.customers },
    suppliers: { name: '供应商', importFn: importAPI.suppliers }
  };

  const config = typeConfig[type];
  if (!config) {
    console.error('不支持的类型:', type);
    return null;
  }

  // 导出数据
  const handleExport = () => {
    const url = exportAPI.getExportUrl(type, exportParams);
    window.open(url, '_blank');
    message.success('开始下载导出文件');
  };

  // 下载导入模板
  const handleDownloadTemplate = () => {
    const url = exportAPI.getTemplateUrl(type);
    window.open(url, '_blank');
    message.success('开始下载导入模板');
  };

  // 打开导入弹窗
  const openImportModal = () => {
    setImportResult(null);
    setImportModalVisible(true);
  };

  // 关闭导入弹窗
  const closeImportModal = () => {
    setImportModalVisible(false);
    setImportResult(null);
  };

  // 处理文件上传
  const handleUpload = async (file) => {
    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await config.importFn(formData);
      setImportResult(response.data);

      if (response.data.success) {
        message.success(response.data.message);
        if (onImportSuccess) {
          onImportSuccess();
        }
      }
    } catch (error) {
      message.error('导入失败: ' + (error.response?.data?.message || error.message));
      setImportResult({
        success: false,
        message: error.response?.data?.message || error.message
      });
    } finally {
      setImporting(false);
    }

    return false; // 阻止自动上传
  };

  // 上传组件配置
  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls',
    showUploadList: false,
    beforeUpload: handleUpload
  };

  return (
    <>
      <Space>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
        >
          导出
        </Button>
        <Button
          icon={<UploadOutlined />}
          onClick={openImportModal}
        >
          导入
        </Button>
      </Space>

      <Modal
        title={`导入${config.name}数据`}
        open={importModalVisible}
        onCancel={closeImportModal}
        footer={[
          <Button key="template" icon={<FileExcelOutlined />} onClick={handleDownloadTemplate}>
            下载导入模板
          </Button>,
          <Button key="close" onClick={closeImportModal}>
            关闭
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="导入说明"
            description={
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li>请先下载导入模板，按照模板格式填写数据</li>
                <li>支持 .xlsx 和 .xls 格式的Excel文件</li>
                <li>文件大小不超过10MB</li>
                <li>标有 * 的列为必填项</li>
                <li>如果{config.name}已存在，将更新现有数据</li>
              </ul>
            }
            type="info"
            showIcon
          />
        </div>

        <Dragger {...uploadProps} disabled={importing}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {importing ? '正在导入...' : '点击或拖拽文件到此区域上传'}
          </p>
          <p className="ant-upload-hint">
            支持 Excel 文件格式 (.xlsx, .xls)
          </p>
        </Dragger>

        {importResult && (
          <div style={{ marginTop: 16 }}>
            {importResult.success ? (
              <Alert
                message="导入完成"
                description={
                  <div>
                    <p>{importResult.message}</p>
                    {importResult.data && (
                      <div>
                        <Text>总计: {importResult.data.total} 条</Text>
                        <br />
                        <Text type="success">成功: {importResult.data.success} 条</Text>
                        <br />
                        <Text type="danger">失败: {importResult.data.failed} 条</Text>
                      </div>
                    )}
                    {importResult.data?.errors?.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="danger">错误详情:</Text>
                        <ul style={{ maxHeight: 150, overflow: 'auto', paddingLeft: 20, margin: '8px 0' }}>
                          {importResult.data.errors.map((err, idx) => (
                            <li key={idx}>
                              第{err.row}行 ({err.code || err.name}): {err.message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                }
                type={importResult.data?.failed > 0 ? 'warning' : 'success'}
                showIcon
              />
            ) : (
              <Alert
                message="导入失败"
                description={importResult.message}
                type="error"
                showIcon
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default ImportExportButtons;
